import { useState, useEffect, useCallback } from 'react'
import {
  SchoolLookUpSiteData,
  NetSuiteCustomer,
  NetSuiteCustomerFilters,
} from 'types'
import ssapi from 'lib/api/secondstepApi'
import {
  EMPTY_NETSUITE_CUSTOMER_SELECTION,
  EMPTY_SELECT_VALUE,
  getDownstreamClearForField,
  isCustomerInLoadedData,
  normalizeSiteData,
  NOT_LISTED_CUSTOMER_ID,
  NOT_LISTED_NETSUITE_CUSTOMER_SELECTION,
  NOT_LISTED_SELECT_VALUE,
  type NormalizedSiteData,
} from './schoolLookUpHelpers'

type ShowRequiredState = Partial<Record<keyof NormalizedSiteData, boolean>>

export const useSchoolLookUp = (
  initialSiteData: SchoolLookUpSiteData,
  onSelection: (data: SchoolLookUpSiteData) => void,
  fireOnLoad: boolean = true,
) => {
  const [selectedSiteData, setSelectedSiteData] = useState<NormalizedSiteData>(
    () => normalizeSiteData(initialSiteData),
  )

  const [hasRegionData, setHasRegionData] = useState<boolean>(true)
  const [showRequired, setShowRequired] = useState<ShowRequiredState>({
    country: false,
    region: false,
    city: false,
    nsCustomerId: false,
    netsuiteCustomerId: false,
    organizationName: false,
  })
  const [netSuiteCustomersData, setNetSuiteCustomersData] = useState<
    NetSuiteCustomer[] | null
  >(null)

  const commitSiteData = useCallback(
    (next: NormalizedSiteData) => {
      setSelectedSiteData(next)
      onSelection(next)
    },
    [onSelection],
  )

  // Call onSelection on mount if fireOnLoad is true and we have initial data
  // But only if we have a netsuiteCustomerId (meaning data is already loaded)
  // or if we don't have an nsCustomerId (meaning no lookup is needed)
  useEffect(() => {
    if (initialSiteData && fireOnLoad) {
      // Only fire onLoad if we have complete data or no lookup is needed
      const hasCompleteData = initialSiteData.netsuiteCustomerId != null
      const noLookupNeeded = !initialSiteData.nsCustomerId

      if (hasCompleteData || noLookupNeeded) {
        onSelection(selectedSiteData)
      }
    }
  }, []) // Only run on mount

  // Update site data when initialSiteData changes
  useEffect(() => {
    if (initialSiteData) {
      setSelectedSiteData(normalizeSiteData(initialSiteData))
    }
  }, [initialSiteData])

  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      const downstreamClear = getDownstreamClearForField(
        field,
        selectedSiteData,
      )
      const updatedData = {
        ...selectedSiteData,
        ...downstreamClear,
        [field]: value,
      }

      commitSiteData(updatedData)
    },
    [selectedSiteData, commitSiteData],
  )

  const handleLoadStateData = useCallback((data: string[]) => {
    setHasRegionData(data.length > 1)
    return data
  }, [])

  const handleLoadSchoolsData = useCallback((data: NetSuiteCustomer[]) => {
    setNetSuiteCustomersData(data)
    return data
  }, [])

  const handleSchoolSelect = useCallback(
    (netSuiteCustomer: NetSuiteCustomer) => {
      const {
        id: netsuiteCustomerId,
        nsCustomerId,
        companyName,
      } = netSuiteCustomer
      const schoolIsListed = netsuiteCustomerId !== NOT_LISTED_CUSTOMER_ID

      commitSiteData({
        ...selectedSiteData,
        companyName: schoolIsListed ? companyName : '',
        netsuiteCustomerId,
        nsCustomerId,
        organizationName: schoolIsListed
          ? ''
          : selectedSiteData.organizationName || '',
      })
    },
    [selectedSiteData, commitSiteData],
  )

  const handleSchoolNameChange = useCallback(
    (organizationName: string) => {
      commitSiteData({
        ...selectedSiteData,
        organizationName,
        netsuiteCustomerId:
          selectedSiteData.netsuiteCustomerId || NOT_LISTED_CUSTOMER_ID,
        nsCustomerId: selectedSiteData.nsCustomerId || '',
      })
    },
    [selectedSiteData, commitSiteData],
  )

  const selectCitiesEndpoint = useCallback(() => {
    if (!selectedSiteData.country) {
      return Promise.resolve({ status: 200, data: [] })
    }

    return ssapi.getCities(selectedSiteData.country, selectedSiteData.region)
  }, [selectedSiteData.region, selectedSiteData.country])

  const selectSchoolsEndpoint = useCallback(() => {
    if (!selectedSiteData.city) {
      return Promise.resolve({ status: 200, data: [] })
    }

    const params: NetSuiteCustomerFilters = {
      country: selectedSiteData.country,
      city: selectedSiteData.city,
    }

    if (hasRegionData && selectedSiteData.region) {
      params.region = selectedSiteData.region
    }

    return ssapi.getNetSuiteCustomers(params)
  }, [
    selectedSiteData.city,
    selectedSiteData.country,
    selectedSiteData.region,
    hasRegionData,
  ])

  const checkRequired = useCallback(
    (field: string, value: string) => {
      if (field === 'region') {
        setShowRequired(prev => ({
          ...prev,
          region: hasRegionData && value === '',
        }))
        return
      }
      setShowRequired(prev => ({ ...prev, [field]: value === '' }))
    },
    [hasRegionData],
  )

  const getNetsuiteCustomerId = useCallback(() => {
    const { netsuiteCustomerId } = selectedSiteData

    if (netsuiteCustomerId === null) {
      return ''
    }

    if (netsuiteCustomerId === NOT_LISTED_CUSTOMER_ID) {
      return NOT_LISTED_SELECT_VALUE
    }

    const value = isCustomerInLoadedData(
      netSuiteCustomersData,
      selectedSiteData,
    )
      ? netsuiteCustomerId
      : NOT_LISTED_CUSTOMER_ID

    return String(value)
  }, [netSuiteCustomersData, selectedSiteData])

  const getNetSuiteCustomerObject = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = event.target.value

      if (selectedValue === '' || selectedValue === EMPTY_SELECT_VALUE) {
        return EMPTY_NETSUITE_CUSTOMER_SELECTION
      }

      if (
        selectedValue === NOT_LISTED_SELECT_VALUE ||
        parseInt(selectedValue, 10) === NOT_LISTED_CUSTOMER_ID
      ) {
        return NOT_LISTED_NETSUITE_CUSTOMER_SELECTION
      }

      const selectedId = parseInt(selectedValue, 10)
      const customer = netSuiteCustomersData?.find(c => c.id === selectedId)

      return {
        id: customer?.id ?? NOT_LISTED_CUSTOMER_ID,
        nsCustomerId: customer?.nsCustomerId ?? '',
        companyName: customer?.companyName ?? '',
      }
    },
    [netSuiteCustomersData],
  )

  return {
    checkRequired,
    getNetSuiteCustomerObject,
    getNetsuiteCustomerId,
    handleFieldChange,
    handleLoadStateData,
    handleLoadSchoolsData,
    handleSchoolNameChange,
    handleSchoolSelect,
    hasRegionData,
    netSuiteCustomersData,
    selectCitiesEndpoint,
    selectSchoolsEndpoint,
    selectedSiteData,
    setNetSuiteCustomersData,
    showRequired,
  }
}
